import type {
  ClientSummary,
  DashboardResponse,
  TicketDetail,
  TicketListItem,
} from "@recursive-staff/schema/contracts";

// Same-origin by default: the API is mounted at `/api/*` on the web's own
// origin (a Worker route), so requests carry the Cloudflare Access session
// cookie automatically and need no CORS. Override with VITE_API_BASE only for
// local dev against a separate API host.
const BASE = import.meta.env.VITE_API_BASE ?? "/api";

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { credentials: "include" });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText} for ${path}`);
  return (await res.json()) as T;
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await errText(res, path));
  return (await res.json()) as T;
}

async function patch<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await errText(res, path));
  return (await res.json()) as T;
}

async function del<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { method: "DELETE", credentials: "include" });
  if (!res.ok) throw new Error(await errText(res, path));
  return (await res.json()) as T;
}

// Prefer the API's {error} message (e.g. "can't remove the last admin") over a
// bare status, so mutation failures surface a useful reason in the UI.
async function errText(res: Response, path: string): Promise<string> {
  try {
    const j = (await res.clone().json()) as { error?: string };
    if (j?.error) return j.error;
  } catch { /* not JSON */ }
  return `${res.status} ${res.statusText} for ${path}`;
}

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  initials: string;
  title: string;
  status: string;
  isAdmin: boolean;
  roleId: string | null;
  roleName: string | null;
  roleSlug: string | null;
  mailboxIds: string[];
}
export interface TeamMembersResponse {
  canManage: boolean;
  viewerIsAdmin: boolean;
  roles: { id: string; slug: string; name: string }[];
  mailboxes: { id: string; label: string; address: string }[];
  members: TeamMember[];
}

export interface DraftResponse {
  draft: string;
  source: "workers-ai" | "template";
  fallback: boolean;
}

export interface AskResponse {
  answer: string;
  sources: string[];
  source: "workers-ai" | "computed";
  fallback: boolean;
}

export interface TicketListResponse {
  openCount: number;
  items: TicketListItem[];
}

export interface ReplyResponse {
  ok: boolean;
  ref: string;
  sent: { ok: boolean; messageId?: string; error?: string };
}

export interface RoadmapItem {
  id: string;
  title: string;
  body: string | null;
  votes: number;
}

export interface RepStatsRow {
  name: string;
  initials: string;
  status: string;
  offered: number;
  accepted: number;
  solved: number;
  firstReply: string;
  handle: string;
  slaMet: number;
  csat: string;
  reopens: number;
  active: string;
  timedOut: number;
  declined: number;
  acceptRate: number;
}

export interface BoardResponse {
  columns: { id: string; label: string }[];
  issues: {
    key: string;
    ticketId: string;
    title: string;
    column: string;
    clientId: string;
    clientName: string;
    by: string;
    status: string;
    tone: "ok" | "warn" | "bad" | "active" | "neutral";
    pr: string | null;
  }[];
}

export interface Client360 {
  id: string;
  name: string;
  host: string;
  env: string;
  plan: string;
  users: number;
  timezone: string;
  health: "ok" | "warn" | "bad";
  dataResidency: string;
  stats: { open: number; atRisk: number; firstReply: string; csat: string } | null;
  openTickets: number;
  people: {
    id: string; name: string; initials: string; email: string | null; phone: string | null; role: string | null;
    status: string; mfa: string; license: string; tags: string[]; openTickets: number;
    facets: Record<string, boolean> | null;
  }[];
  projects: { id: string; name: string; status: string; scheduleHealth: string; progressPct: number; goLiveDate: string | null; pm: string | null }[];
  incidents: { id: string; title: string; severity: string; status: string }[];
}

export interface ProjectRow {
  id: string;
  name: string;
  clientId: string;
  clientName: string;
  status: string;
  scheduleHealth: "ok" | "warn" | "bad" | "active" | "neutral";
  progressPct: number;
  goLiveDate: string | null;
  pm: string | null;
  warnings: { level: string; rule: string; message: string }[];
}

export interface IncidentRow {
  id: string;
  title: string;
  severity: string;
  status: string;
  clientId: string | null;
  clientName: string;
  updates: { status: string | null; body: string }[];
}

export interface ProjectTask {
  id: string;
  title: string;
  status: string;
  assignee: string | null;
  points: number | null;
  dueInDays: number | null;
  blockedForDays: number | null;
}
export interface ProjectDetail {
  id: string;
  name: string;
  client: string;
  clientId: string;
  status: string;
  scheduleHealth: "ok" | "warn" | "bad" | "active" | "neutral";
  progressPct: number;
  goLiveDate: string | null;
  pm: string | null;
  clientContacts: string[];
  approvedHours: number;
  phase: string | null;
  members: { id: string; side: "staff" | "client"; name: string; email: string | null; role: string }[];
  lists: { id: string; name: string; tasks: ProjectTask[] }[];
  time: { billableHours: number; totalHours: number; entries: { id: string; who: string; minutes: number; billable: boolean; note: string | null }[] };
  updates: { id: string; subject: string | null; body: string | null; aiDrafted: boolean; approvedBy: string | null; sentAt: string | null }[];
  warnings: { level: string; rule: string; message: string }[];
  billing: {
    rateCards: { who: string; hourlyRate: number }[];
    invoices: { id: string; status: string; terms: string; total: number; lines: { id: string; who: string; description: string | null; hours: number; rate: number; amount: number }[] }[];
  };
}

export interface ContentProposalVM {
  id: string;
  ref: string;
  kind: "new" | "update" | "video" | "retire";
  state: "suggested" | "drafted" | "review" | "scheduled";
  source: string;
  trigger: string;
  rationale: string;
  confidence: number;
  diff: [string, string][];
  media: [string, string, string][];
  mediaRows: MediaRowVM[];
  autoPublishable: boolean;
  scope: string;
  reviewer: string;
  articleTitle: string;
  articleCategory: string | null;
}
export type MediaState = "not_proposed" | "pending" | "draft" | "ready" | "needs_rerecord";
export interface MediaRowVM {
  id: string;
  kind: "screenshot" | "video" | "storyboard" | "voiceover" | "captions";
  label: string;
  note: string | null;
  state: MediaState;
  assetRef: string | null;
  chapterRef: string | null;
  encoding: boolean; // an upload was minted but not yet promoted (resume polling)
}
export interface ContentStudio {
  queue: ContentProposalVM[];
  sources: { source: string; label: string; enabled: boolean }[];
  policy: { change: string; disposition: string; detail: string; tier: string }[];
}

export interface SigninRow {
  when: string; app: string; location: string; ip: string; device: string;
  method: string; botScore: number; result: string; asn: string; lat: number; lon: number;
}
export interface PersonDetail {
  id: string;
  name: string;
  initials: string;
  email: string | null;
  phone: string | null;
  role: string | null;
  memberKey: string | null;
  clientId: string;
  clientName: string;
  directory: {
    id: string; status: string; mfa: string; license: string; office: string;
    joined: string; lastLogin: string; subscriptions: string[]; tags: string[];
    notes: string; listings: [string, string, string][];
  } | null;
  dossier: {
    realtyId: {
      rid: string; ial: number | null; proofedAt: string | null; searched: string[]; disciplineStatement: string;
      licenses: { state: string; number: string; type: string; status: string; primary: boolean; expires: string | null; asOf?: string | null; eligible?: boolean }[];
    } | null;
    security: {
      mfa: string; threat: string; flags: string[];
      compromised: { source: string; seen: string; action: string } | null;
      cf: { botScoreMin: number; tor: boolean; impossibleTravel: number; countries: string[] };
    } | null;
    billing: { status: string; balance: string; due: string | null; plan: string; invoices: [string, string, string, string][] } | null;
    signins: SigninRow[];
    devices: { id: string; label: string; location: string; lastSeen: string; trust: string; botScore: number }[];
  } | null;
  devices: { id: string; label: string | null; trusted: boolean }[];
  breaches: { source: string | null; detail: string | null }[];
  tickets: { id: string; ref: string; subject: string; status: string; priority: string }[];
  facets: Record<string, boolean> | null;
}

export interface MergeTicket {
  id: string;
  ref: string;
  subject: string;
  client: string;
  status?: string;
}

export type FeedHealthState = "ok" | "alarm" | "degraded";
export interface FeedHealthAlarm { name: string; state: string; reason: string; namespace: string; metric: string; updated: string }
export interface FeedHealthGroup {
  subsystem: string;
  health: FeedHealthState;
  counts: { ok: number; alarm: number; insufficient: number };
  alarms: FeedHealthAlarm[];
}
export type FeedHealthResponse =
  | { configured: false; disabled?: boolean }
  | { configured: true; region: string; overall: FeedHealthState; inAlarm: number; total: number; groups: FeedHealthGroup[] };

export type ArchiveItem = TicketListItem; // hubspotId is now part of TicketListItem
export interface ArchiveResponse { items: ArchiveItem[]; total: number; page: number; pageSize: number; pages: number }

export const api = {
  flags: () => get<Record<string, boolean>>("/flags"),
  // A not-ok token response is an EXPECTED state, not an exception: the flag is
  // off, the Twilio secrets aren't on the Worker, or this user has no phone
  // access. Return {configured:false, reason} so the softphone can say WHICH,
  // instead of get() throwing and the UI collapsing every cause into "not set up".
  telephonyToken: async (): Promise<{ configured: boolean; identity?: string; token?: string; reason?: string }> => {
    const res = await fetch(`${BASE}/telephony/token`, { credentials: "include" });
    // A 2xx with a non-JSON body is almost always a Cloudflare Access login-page
    // redirect (session expiry) — that throws here, which is correct: it's an
    // exceptional auth/network problem, not a settled "configured:false" state.
    if (res.ok) return (await res.json()) as { configured: boolean; identity: string; token?: string };
    // Only the endpoint's DOCUMENTED states become an expected, cacheable
    // configured:false result (each drives its own banner). Anything else — 5xx,
    // 429, a proxy error, or a fetch rejection that never reaches this line — stays
    // exceptional so the query retries instead of pinning the phone "off" for the
    // token's whole TTL.
    let reason = `http_${res.status}`;
    try {
      const j = (await res.json()) as { error?: string };
      if (j?.error) reason = j.error;
    } catch { /* not JSON */ }
    const expected =
      (res.status === 403 && reason === "forbidden") ||
      (res.status === 503 && (reason === "telephony_disabled" || reason === "telephony_not_configured"));
    if (expected) return { configured: false, reason };
    throw new Error(`${res.status} ${res.statusText} for /telephony/token`);
  },
  sendSms: (to: string, body: string) => post<{ ok: boolean; sid: string; ticketId: string | null }>("/telephony/sms", { to, body }),
  transferAgents: () => get<{ id: string; name: string }[]>("/telephony/agents"),
  queueWaiting: () => get<{ waiting: number }>("/telephony/queue"),
  feedHealth: async (): Promise<FeedHealthResponse> => {
    // Treat "flag off" as a first-class not-enabled state, not an error, so the
    // screen renders a friendly message instead of ErrorNote.
    const res = await fetch(`${BASE}/feed-health`, { credentials: "include" });
    if (res.status === 503) return { configured: false, disabled: true };
    if (!res.ok) throw new Error(`${res.status} ${res.statusText} for /feed-health`);
    return (await res.json()) as FeedHealthResponse;
  },
  transferCall: (callSid: string, toIdentity: string, caller: { name: string; kind: string; number: string }) =>
    post<{ ok: boolean }>("/telephony/transfer", { callSid, toIdentity, callerName: caller.name, callerKind: caller.kind, callerNumber: caller.number }),
  person: (id: string) => get<PersonDetail>(`/people/${id}`),
  dashboard: (client?: string) => get<DashboardResponse>(`/dashboard${client ? `?client=${client}` : ""}`),
  tickets: (params: { client?: string; view?: string; channel?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.client) q.set("client", params.client);
    if (params.view) q.set("view", params.view);
    if (params.channel) q.set("channel", params.channel);
    const qs = q.toString();
    return get<TicketListResponse>(`/tickets${qs ? `?${qs}` : ""}`);
  },
  // Record-keeping archive: paginated, SQL-searched view of imported (archival) tickets.
  archive: (params: { q?: string; page?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.q) qs.set("q", params.q);
    if (params.page) qs.set("page", String(params.page));
    const s = qs.toString();
    return get<ArchiveResponse>(`/tickets/archive${s ? `?${s}` : ""}`);
  },
  ticket: (id: string) => get<TicketDetail>(`/tickets/${encodeURIComponent(id)}`),
  // Safely-sanitized HTML body for one inbound email message (rendered in a
  // sandboxed iframe). Null html = no HTML part / not available.
  messageBody: (ticketId: string, messageId: string) =>
    get<{ html: string | null; hasHtml: boolean; blockedRemoteImages?: number; inertLinks?: boolean }>(
      `/tickets/${encodeURIComponent(ticketId)}/messages/${encodeURIComponent(messageId)}/body`,
    ),
  // Trusted-vendor allowlist (identity-only; never exempts payload scanning).
  trustedVendors: () => get<{ vendors: string[]; canManage: boolean }>("/trusted-vendors"),
  addTrustedVendor: (entry: string) => post<{ ok?: boolean; error?: string; vendors?: string[] }>("/trusted-vendors", { entry }),
  removeTrustedVendor: (entry: string) => del<{ ok: boolean; vendors: string[] }>(`/trusted-vendors?entry=${encodeURIComponent(entry)}`),
  ticketTime: (id: string) =>
    get<{
      totalMinutes: number;
      entries: { id: string; who: string; minutes: number; billable: boolean; note: string | null; at: string | null }[];
    }>(`/tickets/${encodeURIComponent(id)}/time`),
  logTime: (id: string, minutes: number, billable: boolean, note: string) =>
    post<{ ok: boolean; id: string; totalMinutes: number }>(`/tickets/${encodeURIComponent(id)}/time`, { minutes, billable, note }),
  clients: () => get<ClientSummary[]>("/clients"),
  client: (id: string) => get<Client360>(`/clients/${id}`),
  mlsOps: (id: string) => get<{
    client: string;
    users: { total: number; realtors: number; mlsMembers: number; emailVerified: number };
    offices: { key: string; name: string; agents: number }[];
    licenseStates: { state: string; count: number }[];
  }>(`/clients/${encodeURIComponent(id)}/mls-ops`),
  clientMembers: (id: string, q?: string) =>
    get<{
      count: number;
      members: { id: string; name: string; email: string | null; phone: string | null; role: string | null; realtor: boolean; mlsMember: boolean }[];
    }>(`/clients/${encodeURIComponent(id)}/members${q && q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ""}`),
  officeRoster: (id: string, key: string) =>
    get<{
      key: string;
      truncated: boolean;
      agents: { id: string; name: string; email: string | null; role: string | null; realtor: boolean; mlsMember: boolean }[];
    }>(`/clients/${encodeURIComponent(id)}/office-roster?key=${encodeURIComponent(key)}`),
  team: () => get<RepStatsRow[]>("/team"),
  board: () => get<BoardResponse>("/board"),
  kb: () => get<{ id: string; title: string; kind: string; scope: string; views: number; updated: string | null }[]>("/kb"),
  kbArticle: (id: string) =>
    get<{
      id: string; title: string; kind: string; scope: string | null; views: number; updated: string | null;
      subtitle: string | null; body: string | null; tags: string[];
      related: { id: string; title: string; kind: string }[];
    }>(`/kb/${encodeURIComponent(id)}`),
  macros: () => get<{ id: string; name: string; category: string | null; uses: number }[]>("/macros"),
  tenantClients: () => get<{ id: string; name: string }[]>("/tenants"),
  templates: () => get<{ id: string; name: string; taskCount: number; lists: string[] }[]>("/templates"),
  createTemplate: (name: string, tasks: { list: string; title: string }[]) => post<{ ok: boolean; id: string; taskCount: number }>("/templates", { name, tasks }),
  applyTemplate: (id: string, clientId: string, name: string) => post<{ ok: boolean; projectId: string }>(`/templates/${encodeURIComponent(id)}/apply`, { clientId, name }),
  attention: () => get<{
    count: number;
    items: { id: string; ref: string; subject: string; clientId: string; priority: string; reasons: string[]; since: string | null }[];
  }>("/attention"),
  oncall: () => get<{
    ring: { id: string; position: number; target: string; ringSeconds: number }[];
    recent: { id: string; number: string | null; outcome: string | null; ticketRef: string | null; at: string | null }[];
  }>("/oncall"),
  // Operations tools
  flows: () => get<{ id: string; name: string; mlsId: string | null; active: boolean; steps: unknown[]; stepCount: number }[]>("/flows"),
  createFlow: (name: string) => post<{ ok: boolean; id: string }>("/flows", { name }),
  toggleFlow: (id: string) => post<{ ok: boolean; active: boolean }>(`/flows/${encodeURIComponent(id)}/toggle`, {}),
  bulkSends: () => get<{ id: string; macro: string; recipients: number; status: string; requestedBy: string; approvedBy: string | null; needsApproval: boolean }[]>("/bulk"),
  createBulk: (macroId: string, recipients: number) => post<{ ok: boolean; id: string; status: string; needsApproval: boolean }>("/bulk", { macroId, recipients }),
  approveBulk: (id: string) => post<{ ok: boolean; status: string }>(`/bulk/${encodeURIComponent(id)}/approve`, {}),
  // Duplicates & Merge
  mergeOverview: () =>
    get<{
      dedupeActive: boolean;
      suggestions: { id: string; confidence: number | null; primary: MergeTicket; duplicate: MergeTicket }[];
      recent: { id: string; actor: string | null; at: string | null; reversedAt: string | null; reversible: boolean; primary: MergeTicket; merged: MergeTicket }[];
    }>("/merge"),
  mergeCandidates: (q?: string) =>
    get<{ items: MergeTicket[] }>(`/merge/candidates${q && q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ""}`),
  merge: (primaryTicketId: string, duplicateTicketId: string, suggestionId?: string) =>
    post<{ ok: boolean; id: string }>("/merge", { primaryTicketId, duplicateTicketId, ...(suggestionId ? { suggestionId } : {}) }),
  reverseMerge: (id: string) => post<{ ok: boolean }>(`/merge/${encodeURIComponent(id)}/reverse`, {}),
  reports: () => get<{
    totals: { total: number; open: number; solved: number };
    byClient: { clientId: string; open: number; total: number }[];
    byChannel: { channel: string; count: number }[];
    csat: { average: number | null; responses: number; byClient: { clientId: string; average: number; responses: number }[] };
  }>("/reports"),
  parity: () => get<{ area: string; clientAdmin: string; staff: string }[]>("/parity"),
  vendors: () => get<{ id: string; name: string; category: string | null; status: string }[]>("/vendors"),
  projects: () => get<ProjectRow[]>("/projects"),
  createProject: (name: string, clientId: string) => post<{ ok: boolean; id: string }>("/projects", { name, clientId }),
  incidents: () => get<IncidentRow[]>("/incidents"),
  contentStudio: () => get<ContentStudio>("/content/studio"),
  resolveProposal: (id: string, action: "publish" | "reject", reason?: string) =>
    post<{ ok: boolean; resolution: string; articleId?: string }>(`/content/proposals/${encodeURIComponent(id)}/resolve`, { action, reason }),
  generateContent: () => post<{ ok: boolean; generated: number; scanned: number; autoPublished: number }>("/content/generate", {}),
  transitionMedia: (proposalId: string, mediaId: string, patch: { state?: MediaState; assetRef?: string; expectedAssetRef?: string }) =>
    post<{ ok: boolean; state?: string; assetRef?: string }>(`/content/proposals/${encodeURIComponent(proposalId)}/media/${encodeURIComponent(mediaId)}`, patch),
  // Cloudflare Stream: mint a direct-upload for a video media row, then poll until ready.
  streamUpload: (proposalId: string, mediaId: string, size: number) =>
    post<{ ok: boolean; uid: string; uploadURL: string }>(`/content/proposals/${encodeURIComponent(proposalId)}/media/${encodeURIComponent(mediaId)}/stream-upload`, { size }),
  streamStatus: (proposalId: string, mediaId: string) =>
    get<{ ready: boolean; state?: string; uid?: string; duration?: string; error?: string }>(`/content/proposals/${encodeURIComponent(proposalId)}/media/${encodeURIComponent(mediaId)}/stream-status`),
  project: (id: string) => get<ProjectDetail>(`/projects/${encodeURIComponent(id)}`),
  inviteProjectMember: (id: string, m: { name: string; email: string; role: string }) =>
    post<{ ok: boolean; id: string; already?: boolean }>(`/projects/${encodeURIComponent(id)}/members`, m),
  removeProjectMember: (id: string, memberId: string) =>
    del<{ ok: boolean }>(`/projects/${encodeURIComponent(id)}/members/${encodeURIComponent(memberId)}`),
  status: (clientId: string) =>
    get<{
      client: string;
      clientId: string;
      overall: "operational" | "degraded" | "major_outage";
      subscribers: { total: number; sms: number; email: number };
      incidents: { id: string; title: string; severity: string; status: string; resolved: boolean; updates: { status: string | null; body: string | null; at: string }[] }[];
    }>(`/status/${encodeURIComponent(clientId)}`),
  mailboxes: () =>
    get<{
      routing: { domain: string | null; configured: boolean; canManage: boolean };
      mailboxes: { id: string; address: string; label: string; signoff: string; color: string; fromName: string | null; replyTo: string | null; members: string[]; signatures: number }[];
    }>("/mailboxes"),
  createMailbox: (input: { local?: string; address?: string; label: string; signoff?: string; color?: string; fromName?: string; replyTo?: string }) =>
    post<{ ok: boolean; id: string; address: string; label: string; signoff: string; color: string; routing: { routed: boolean; reason?: string } }>("/mailboxes", input),
  updateMailbox: (id: string, input: { label?: string; signoff?: string; color?: string; fromName?: string; replyTo?: string }) =>
    patch<{ ok: boolean }>(`/mailboxes/${encodeURIComponent(id)}`, input),
  automations: () =>
    get<{
      automations: { name: string; trigger: string; action: string; on: boolean; runs: number }[];
      sla: { priority: string; firstReply: string; resolve: string; coverage: string }[];
      csat: { scale: string; cadence: string; triggers: [string, string][]; lowScore: string; current: { avg: string; responses: number; period: string } };
    }>("/automations"),
  schedule: () =>
    get<{
      tiers: { tier: string; role: string; coverage: string; reps: string[]; escalateWhen: string }[];
      days: string[];
      shifts: [string, number, string[]][];
      gaps: string[];
      onCall: [string, string, string][];
    }>("/schedule"),
  hours: () =>
    get<{
      canManage: boolean;
      clients: { id: string; name: string; timezone: string; windows: { weekday: number; day: string; open: string; close: string }[] }[];
      holidays: { id: string; name: string; date: string; scope: string; clientId: string | null }[];
    }>("/hours"),
  setHours: (clientId: string, windows: { weekday: number; open: string | null; close: string | null }[]) =>
    post<{ ok: boolean }>(`/hours/${encodeURIComponent(clientId)}`, { windows }),
  addHoliday: (name: string, date: string, scope?: string) => post<{ ok: boolean; id: string }>("/holidays", { name, date, scope }),
  deleteHoliday: (id: string) => del<{ ok: boolean }>(`/holidays/${encodeURIComponent(id)}`),
  permissions: () =>
    get<{
      capabilities: { area: string; key: string; label: string }[];
      canEdit: boolean;
      roles: { id: string; slug: string; name: string; staff: number; allowed: string[] }[];
    }>("/permissions"),
  togglePermission: (roleId: string, capability: string, allowed: boolean) =>
    post<{ ok: boolean }>("/permissions", { roleId, capability, allowed }),
  teamMembers: () => get<TeamMembersResponse>("/team/members"),
  addTeamMember: (m: { name: string; email: string; roleSlug?: string; isAdmin?: boolean; title?: string; mailboxIds?: string[] }) =>
    post<{ ok: boolean; id: string }>("/team/members", m),
  updateTeamMember: (id: string, m: { name?: string; title?: string; roleSlug?: string; isAdmin?: boolean; mailboxIds?: string[] }) =>
    patch<{ ok: boolean }>(`/team/members/${id}`, m),
  audit: (params: { event?: string; client?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.event) q.set("event", params.event);
    if (params.client) q.set("client", params.client);
    const qs = q.toString();
    return get<{
      events: { id: string; actor: string; event: string; targetKind: string | null; targetId: string | null; client: string | null; clientId: string | null; ip: string | null; ua: string | null; hasDiff: boolean; at: string }[];
      eventTypes: string[];
    }>(`/audit${qs ? `?${qs}` : ""}`);
  },
  retention: () =>
    get<{
      rules: { id: string; scope: string; entity: string; retainDays: number; retain: string }[];
      legalHolds: { id: string; client: string; clientId: string | null; reason: string | null; since: string }[];
      clients: { id: string; name: string; residency: string; plan: string; legalHold: boolean }[];
      exports: { id: string; client: string; clientId: string | null; status: string; requestedBy: string; at: string }[];
    }>("/retention"),
  routerLog: () =>
    get<{ log: { id: string; ref: string; tier: number; rep: string | null; outcome: string; tone: "ok" | "warn" | "bad" | "active" | "neutral" | null; at: string }[] }>("/router/log"),
  updates: () =>
    get<{
      releases: { id: string; version: string; publishedAt: string | null; note: string | null; items: { kind: string; title: string; body: string | null; reportedBy: string | null }[] }[];
      roadmap: { now: RoadmapItem[]; next: RoadmapItem[]; later: RoadmapItem[] };
      announcements: { id: string; title: string; body: string | null; channels: string[]; scheduledAt: string | null; scheduled: boolean; approvedBy: string | null; readCount: number; totalClients: number }[];
    }>("/updates"),
  phone: () =>
    get<{
      calls: { id: string; direction: string; who: string | null; number: string | null; when: string | null; duration: string | null; outcome: string | null; handledBy: string | null; client: string | null; ref: string | null; hasRecording: boolean }[];
      sms: { id: string; who: string | null; number: string | null; lastMessage: string | null; when: string | null; client: string | null; ref: string | null }[];
      chats: { id: string; visitor: string | null; initials: string | null; role: string | null; page: string | null; browser: string | null; status: string; wait: string | null; unread: number; client: string | null }[];
    }>("/phone"),
  themes: () =>
    get<{
      themes: { tag: string; count: number; open: number; urgent: number; tone: "ok" | "warn" | "bad"; clients: string[]; examples: { ref: string; subject: string }[] }[];
      totalTickets: number;
    }>("/themes"),
  deflect: () =>
    get<{
      funnel: { period: string; sessions: number; articleViews: number; aiAnswers: number; deflected: number; ticketsCreated: number; deflectionRate: number; trend: number };
      guardrails: { rule: string; on: boolean; detail: string }[];
      widget: { greeting: string; placeholder: string; handoffLabel: string; accent: string };
      topArticles: { id: string; title: string; kind: string; views: number; scope: string | null }[];
    }>("/deflect"),
  draft: (ticketId: string) => post<DraftResponse>("/ai/draft", { ticketId }),
  ask: (question: string) => post<AskResponse>("/ai/ask", { question }),
  voteRoadmap: (id: string) =>
    post<{ ok: boolean; votes: number }>(`/roadmap/${encodeURIComponent(id)}/vote`, {}),
  promoteTheme: (tag: string) =>
    post<{ ok: boolean; id: string; alreadyOnRoadmap: boolean }>("/themes/promote", { tag }),
  reply: (ticketId: string, text: string) =>
    post<ReplyResponse>(`/tickets/${encodeURIComponent(ticketId)}/reply`, { text }),
  setStatus: (ticketId: string, status: string) =>
    post<{ ok: boolean; ref: string; status: string }>(`/tickets/${encodeURIComponent(ticketId)}/status`, { status }),
  escalate: (ticketId: string) =>
    post<{ ok: boolean; ref: string; tier: number }>(`/tickets/${encodeURIComponent(ticketId)}/escalate`, {}),
};
